import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { ArrowLeft, Save, Flame, TrendingUp } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { SymptomHeatmap } from '@/components/shared/SymptomHeatmap';
import { API } from '@/lib/api';



export default function MenopauseModule() {
  const { language } = useLanguage();
  const { getHeaders } = useAuth();
  const nav = useNavigate();
  const today = format(new Date(), 'yyyy-MM-dd');
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [correlations, setCorrelations] = useState(null);
  const [log, setLog] = useState({
    date: today, hot_flashes: 0, night_sweats: 0, mood_swings: 3, sleep_quality: 3,
    joint_pain: 1, brain_fog: 1, dryness: 1, triggers: [], notes: ''
  });

  const de = language === 'de';
  const triggerOpts = de
    ? ['Zucker', 'Alkohol', 'Kaffee', 'Stress', 'Schlafmangel', 'Sport', 'Hitze', 'Scharfes Essen']
    : ['Sugar', 'Alcohol', 'Coffee', 'Stress', 'Sleep deprivation', 'Exercise', 'Heat', 'Spicy food'];

  const symptomLabels = {
    hot_flashes: de ? 'Hitzewallungen' : 'Hot flashes',
    night_sweats: de ? 'Nachtschweiss' : 'Night sweats',
    mood_swings: de ? 'Stimmung' : 'Mood',
    sleep_quality: de ? 'Schlaf' : 'Sleep',
    brain_fog: de ? 'Brain Fog' : 'Brain fog',
    joint_pain: de ? 'Gelenke' : 'Joints',
  };

  const fetchData = useCallback(async () => {
    try {
      const h = getHeaders();
      const [logRes, histRes, corrRes] = await Promise.all([
        axios.get(`${API}/modules/menopause/log/${today}`, { headers: h }),
        axios.get(`${API}/modules/menopause/history?days=90`, { headers: h }),
        axios.get(`${API}/modules/menopause/correlations`, { headers: h }).catch(() => ({ data: null })),
      ]);
      if (logRes.data?.hot_flashes !== undefined) {
        setLog(prev => ({ ...prev, ...logRes.data }));
      }
      setHistory(histRes.data.reverse());
      if (corrRes.data) setCorrelations(corrRes.data);
    } catch (e) { console.error(e); }
  }, [today]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/modules/menopause/log`, log, { headers: getHeaders() });
      toast.success(de ? 'Gespeichert!' : 'Saved!');
      fetchData();
    } catch { toast.error(de ? 'Fehler' : 'Error'); }
    setSaving(false);
  };

  const toggleTrigger = (t) => setLog(prev => ({
    ...prev, triggers: prev.triggers.includes(t) ? prev.triggers.filter(x => x !== t) : [...prev.triggers, t]
  }));

  const chartData = history.map(h => ({
    date: h.date?.slice(5),
    hf: h.hot_flashes || 0,
    ns: h.night_sweats || 0,
    mood: h.mood_swings || 0,
    sleep: h.sleep_quality || 0,
  }));

  return (
    <div className="space-y-6 pb-24 lg:pb-8 max-w-3xl" data-testid="menopause-module">
      <div className="flex items-center gap-3">
        <button onClick={() => nav('/dashboard')} className="text-foreground/40 hover:text-foreground transition-colors" data-testid="menopause-back"><ArrowLeft size={20} /></button>
        <div>
          <Badge className="bg-orange-100 text-orange-700 border-0 rounded-full font-body text-xs mb-1">{de ? 'Modul 4' : 'Module 4'}</Badge>
          <h1 className="font-display text-3xl font-medium tracking-tight">{de ? 'Peri-/Menopause' : 'Peri-/Menopause'}</h1>
        </div>
      </div>

      {/* Today's Symptoms */}
      <Card className="bg-white border border-border/60 rounded-2xl p-6 shadow-sm" data-testid="menopause-symptoms-card">
        <h2 className="font-display text-xl font-medium mb-4">{de ? 'Symptome heute' : 'Symptoms Today'}</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="font-body text-sm text-foreground/70 flex items-center gap-2">
              <Flame size={14} className="text-orange-500" />
              {de ? 'Hitzewallungen' : 'Hot Flashes'}: {log.hot_flashes}x
            </Label>
            <div className="flex gap-2 mt-2">
              {[0, 1, 2, 3, 4, 5, '6+'].map((n, i) => (
                <button key={i} onClick={() => setLog({ ...log, hot_flashes: typeof n === 'number' ? n : 6 })}
                  className={`w-9 h-9 rounded-lg font-body text-sm font-medium transition-all ${log.hot_flashes === (typeof n === 'number' ? n : 6) ? 'bg-orange-500 text-white' : 'bg-warmgray/50 text-foreground/60 hover:bg-orange-100'}`}
                  data-testid={`hf-${n}`}>{n}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="font-body text-sm text-foreground/70">{de ? 'Nachtschweiss' : 'Night Sweats'}: {log.night_sweats}x</Label>
            <div className="flex gap-2 mt-2">
              {[0, 1, 2, 3, 4, 5].map(n => (
                <button key={n} onClick={() => setLog({ ...log, night_sweats: n })}
                  className={`w-9 h-9 rounded-lg font-body text-sm font-medium transition-all ${log.night_sweats === n ? 'bg-orange-500 text-white' : 'bg-warmgray/50 text-foreground/60 hover:bg-orange-100'}`}
                  data-testid={`ns-${n}`}>{n}</button>
              ))}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 mt-5">
          {[
            { key: 'mood_swings', label: de ? 'Stimmung' : 'Mood', max: 10 },
            { key: 'sleep_quality', label: de ? 'Schlafqualität' : 'Sleep Quality', max: 10 },
            { key: 'brain_fog', label: 'Brain Fog', max: 10 },
            { key: 'joint_pain', label: de ? 'Gelenkschmerzen' : 'Joint Pain', max: 10 },
          ].map(s => (
            <div key={s.key}>
              <Label className="font-body text-sm text-foreground/70">{s.label}: {log[s.key]}/{s.max}</Label>
              <Slider value={[log[s.key]]} onValueChange={([v]) => setLog({ ...log, [s.key]: v })} min={0} max={s.max} step={1}
                className="mt-2 [&_[role=slider]]:bg-orange-500 [&_[role=slider]]:border-orange-500" data-testid={`slider-${s.key}`} />
            </div>
          ))}
        </div>
      </Card>

      {/* Triggers */}
      <Card className="bg-white border border-border/60 rounded-2xl p-6" data-testid="menopause-triggers-card">
        <h2 className="font-display text-xl font-medium mb-3">{de ? 'Auslöser heute' : 'Triggers Today'}</h2>
        <div className="flex flex-wrap gap-2">
          {triggerOpts.map(t => (
            <button key={t} onClick={() => toggleTrigger(t)}
              className={`px-3 py-1.5 rounded-full font-body text-sm transition-all border ${log.triggers.includes(t) ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-foreground/60 border-border/60 hover:border-orange-300'}`}
              data-testid={`trigger-${t}`}>{t}</button>
          ))}
        </div>
      </Card>

      {/* Trigger Correlations */}
      {correlations?.correlations?.length > 0 && (
        <Card className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200/50 rounded-2xl p-6" data-testid="menopause-correlations">
          <h2 className="font-display text-lg font-medium mb-3 flex items-center gap-2">
            <TrendingUp size={18} className="text-orange-500" />
            {de ? 'Deine Trigger-Analyse' : 'Your Trigger Analysis'}
          </h2>
          <p className="font-body text-xs text-foreground/50 mb-4">{de ? `Basierend auf ${correlations.total_entries} Einträgen` : `Based on ${correlations.total_entries} entries`}</p>
          <div className="space-y-2">
            {correlations.correlations.map((c, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-white/70">
                <span className="font-body text-sm font-medium text-foreground/80 shrink-0 w-28">{c.trigger} ({c.count}x)</span>
                <div className="flex flex-wrap gap-1.5">
                  {c.findings.map((f, j) => (
                    <span key={j} className={`px-2 py-0.5 rounded-full font-body text-[11px] font-medium ${f.change_pct > 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                      {symptomLabels[f.symptom] || f.symptom} {f.change_pct > 0 ? '+' : ''}{f.change_pct}%
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Symptom Heatmap */}
      {history.length > 7 && (
        <Card className="bg-white border border-border/60 rounded-2xl p-6" data-testid="menopause-heatmap">
          <SymptomHeatmap
            data={history}
            metric="hot_flashes"
            title={de ? 'Hitzewallungen (letzte 4 Wochen)' : 'Hot Flashes (last 4 weeks)'}
          />
        </Card>
      )}

      {/* Trend Chart */}
      {chartData.length > 3 && (
        <Card className="bg-white border border-border/60 rounded-2xl p-6" data-testid="menopause-chart">
          <h2 className="font-display text-lg font-medium mb-4">{de ? 'Symptom-Verlauf' : 'Symptom Trends'}</h2>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D6" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fontFamily: 'Mulish' }} />
              <YAxis tick={{ fontSize: 11, fontFamily: 'Mulish' }} />
              <Tooltip contentStyle={{ borderRadius: 12, fontFamily: 'Mulish', fontSize: 12 }} />
              <Legend wrapperStyle={{ fontFamily: 'Mulish', fontSize: 11 }} />
              <Area type="monotone" dataKey="hf" name={de ? 'Hitzewallungen' : 'Hot flashes'} stroke="#F97316" fill="#FED7AA" strokeWidth={2} />
              <Area type="monotone" dataKey="mood" name={de ? 'Stimmung' : 'Mood'} stroke="#EC4899" fill="#FBCFE8" strokeWidth={1.5} />
              <Area type="monotone" dataKey="sleep" name={de ? 'Schlaf' : 'Sleep'} stroke="#8B5CF6" fill="#DDD6FE" strokeWidth={1.5} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      )}

      <Textarea value={log.notes} onChange={e => setLog({ ...log, notes: e.target.value })} className="rounded-xl" rows={2} placeholder={de ? 'Notizen...' : 'Notes...'} data-testid="menopause-notes" />

      <Button onClick={handleSave} disabled={saving} className="w-full md:w-auto rounded-full bg-orange-500 hover:bg-orange-600 text-white px-10 py-5 text-base font-medium shadow-lg shadow-orange-500/20" data-testid="menopause-save-btn">
        <Save size={18} className="mr-2" /> {saving ? '...' : de ? 'Speichern' : 'Save'}
      </Button>
    </div>
  );
}
